/**
 * Une fotos en un solo PDF, una por página, en el navegador.
 *
 * Para las tareas hechas a mano: el estudiante fotografía sus hojas con el
 * teléfono y entrega un PDF, igual que si lo hubiera escaneado. Cada foto se
 * gira según su orientación real, se reduce a un tamaño razonable y se pasa a
 * JPEG; así diez fotos de un teléfono moderno no pesan cien megas.
 */

/** Lado mayor de cada foto, en píxeles: se sigue leyendo bien la letra. */
const MAX_SIDE = 2000;
const QUALITY = 0.82;
/** Carta, en puntos: lo que se imprime en el colegio. */
const PAGE = { w: 612, h: 792 };
const MARGIN = 18;

export const isPhoto = (f: File) => f.type.startsWith('image/') || /\.(jpe?g|png|heic|heif|webp)$/i.test(f.name);

async function toJpeg(file: File): Promise<{ bytes: Uint8Array; width: number; height: number }> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    throw new Error(
      `No se pudo leer la foto «${file.name}». Si es HEIC (iPhone), cámbiala a JPG o hazle una captura de pantalla.`,
    );
  }
  const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  // Fondo blanco: una PNG con transparencia saldría negra en JPEG.
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY));
  if (!blob) throw new Error(`No se pudo preparar la foto «${file.name}».`);
  return { bytes: new Uint8Array(await blob.arrayBuffer()), width, height };
}

export async function photosToPdf(
  photos: File[],
  fileName: string,
  onStep?: (done: number, total: number) => void,
): Promise<File> {
  // pdf-lib solo hace falta cuando alguien entrega fotos: se carga entonces.
  const { PDFDocument } = await import('pdf-lib');
  const pdf = await PDFDocument.create();
  for (let i = 0; i < photos.length; i += 1) {
    onStep?.(i, photos.length);
    const jpg = await toJpeg(photos[i]);
    const img = await pdf.embedJpg(jpg.bytes);
    // Hoja apaisada si la foto lo es; la foto, centrada y lo más grande posible.
    const landscape = jpg.width > jpg.height;
    const pw = landscape ? PAGE.h : PAGE.w;
    const ph = landscape ? PAGE.w : PAGE.h;
    const fit = Math.min((pw - 2 * MARGIN) / jpg.width, (ph - 2 * MARGIN) / jpg.height);
    const w = jpg.width * fit;
    const h = jpg.height * fit;
    pdf.addPage([pw, ph]).drawImage(img, { x: (pw - w) / 2, y: (ph - h) / 2, width: w, height: h });
  }
  onStep?.(photos.length, photos.length);
  const bytes = await pdf.save();
  return new File([bytes.slice().buffer as ArrayBuffer], fileName, { type: 'application/pdf' });
}
